import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ThemeToggle } from "@/components/ThemeToggle";
import { IdentificationCard, Lock, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";
import { PasswordRequirements } from "@/components/PasswordRequirements";
import { PasswordInput } from "@/components/PasswordInput";
import { isPasswordValid, PASSWORD_POLICY_MSG } from "@/lib/password";

const LOGO = "/org_logo.png";
const LOGO_BLEND = "/org_logo_blend.png";

const HERO = "https://images.unsplash.com/photo-1518152006812-edab29b069ac?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

export default function Login() {
  const { login, refreshUser, user } = useAuth();
  const navigate = useNavigate();
  const [staffId, setStaffId] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  // forced password change state
  const [forceChange, setForceChange] = useState(false);
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");

  const routeByRole = (u) => navigate(u.role === "member" ? "/profile" : "/dashboard");

  const submitLogin = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const u = await login(staffId, password);
      if (u.must_change_password) {
        setForceChange(true);
      } else {
        toast.success(`Welcome, ${u.first_name}`);
        routeByRole(u);
      }
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const submitChange = async (e) => {
    e.preventDefault();
    setError("");
    if (!isPasswordValid(newPw)) { setError(PASSWORD_POLICY_MSG); return; }
    if (newPw !== confirmPw) { setError("Passwords do not match"); return; }
    setBusy(true);
    try {
      await api.post("/auth/change-password", { new_password: newPw });
      const u = await refreshUser();
      toast.success("Password updated. Welcome aboard!");
      routeByRole(u);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      {/* Left hero */}
      <div className="relative hidden lg:block">
        <img src={HERO} alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/85 to-red-950/50" />
        <div className="relative z-10 flex h-full flex-col justify-between p-12 text-white">
          <Link to="/" data-testid="login-hero-home-link" aria-label="MELPWU home" className="flex items-center gap-3 w-fit">
            <img src={LOGO} alt="MELPWU logo" className="h-16 w-16 rounded-xl bg-white p-1.5 object-contain shadow-xl ring-1 ring-white/20" />
            <span className="font-display text-2xl font-bold tracking-tight">MELPWU MMS</span>
          </Link>
          <div className="max-w-md">
            <h1 className="font-display text-4xl xl:text-5xl font-extrabold tracking-tight leading-none">
              Membership Management System
            </h1>
          </div>
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-white/50">
            We Cannot Fail
          </p>
        </div>
      </div>

      {/* Right form */}
      <div className="flex flex-col justify-center px-6 py-10 sm:px-12 lg:px-16">
        <div className="absolute top-6 right-6"><ThemeToggle /></div>
        <div className="mx-auto w-full max-w-sm animate-fade-up">
          <Link to="/" data-testid="login-logo-home-link" aria-label="MELPWU home" className="block w-fit mx-auto mb-8">
          <img src={LOGO_BLEND} alt="MELPWU logo" data-testid="login-logo"
            className="h-40 w-40 object-contain block" />
          </Link>

          {!forceChange ? (
            <>
              <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Sign in</span>
              <h2 className="font-display text-3xl font-bold tracking-tight mt-1">Welcome back</h2>

              <form onSubmit={submitLogin} className="mt-8 space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="staffId">Staff ID or email address</Label>
                  <div className="relative">
                    <IdentificationCard size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <Input id="staffId" data-testid="login-staffid-input" value={staffId}
                      onChange={(e) => setStaffId(e.target.value)} placeholder="Staff ID or registered email"
                      className="pl-10 font-mono" autoComplete="username" required />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="password">Password / SSNIT Number</Label>
                  <PasswordInput leftIcon id="password" testId="login-password-input" value={password}
                    onChange={(e) => setPassword(e.target.value)} placeholder="••••••••"
                    autoComplete="current-password" required />
                </div>
                {error && (
                  <div data-testid="login-error" className="flex items-start gap-2 rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
                    <Warning size={18} className="mt-0.5 shrink-0" /> {error}
                  </div>
                )}
                <Button type="submit" data-testid="login-submit-button" disabled={busy} className="w-full h-11 text-base">
                  {busy ? "Signing in…" : "Sign in"}
                </Button>
                <button type="button" data-testid="forgot-password-link"
                  onClick={() => navigate("/forgot-password")}
                  className="block w-full text-center text-sm text-muted-foreground hover:text-primary transition-colors">
                  Forgot your password?
                </button>
              </form>
              <div className="mt-7 pt-6 border-t border-border" data-testid="portal-entry-section">
                <p className="text-sm text-muted-foreground mb-3">New applicant? Create an account to start your membership application.</p>
                <Button onClick={() => navigate("/join")} data-testid="create-account-button" className="w-full h-auto min-h-12 whitespace-normal text-base">Create Account</Button>
              </div>
            </>
          ) : (
            <>
              <span className="text-xs uppercase tracking-[0.2em] text-primary">First-time setup</span>
              <h2 className="font-display text-3xl font-bold tracking-tight mt-1">Set a new password</h2>
              <p className="text-sm text-muted-foreground mt-2">
                For your security, choose a new password. It must be different from your SSNIT number.
              </p>
              <form onSubmit={submitChange} className="mt-8 space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="newPw">New password</Label>
                  <PasswordInput id="newPw" testId="new-password-input" value={newPw}
                    onChange={(e) => setNewPw(e.target.value)} placeholder="Create a strong password" required />
                  <PasswordRequirements value={newPw} className="pt-1" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="confirmPw">Confirm new password</Label>
                  <PasswordInput id="confirmPw" testId="confirm-password-input" value={confirmPw}
                    onChange={(e) => setConfirmPw(e.target.value)} placeholder="Re-enter password" required />
                </div>
                {error && (
                  <div data-testid="change-error" className="flex items-start gap-2 rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
                    <Warning size={18} className="mt-0.5 shrink-0" /> {error}
                  </div>
                )}
                <Button type="submit" data-testid="change-password-submit" disabled={busy} className="w-full h-11 text-base">
                  {busy ? "Saving…" : "Save & continue"}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
